import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import { getDealerBasePath } from "../../navigation/getDealerBasePath";
import {
  HunterTireItem,
  useGetHunterTire,
} from "../../queries/useGetHunterTire";
import { useHunterTireSelectedIds } from "../../stores/hooks/useHunterTireSelectedId";
import {
  DEALER_DATE_TIME,
  DateDisplay,
} from "../../ui/DateDisplay/DateDisplay";
import { Placeholder } from "../../ui/Placeholder";
import { ChevronRightIcon } from "../../ui/icons/ChevronRightIcon";
import { TableHeader } from "../ColumnSort/TableHeader";
import { NoData } from "../NoData/NoData";
import { HunterTireRow } from "./HunterTireRow";
import { HunterTreadDepthCell } from "./HunterTreadDepthCell";
import { formatRimSize } from "./hunterTireFormatting";

const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/i;

const placeholders: HunterTireItem[] = [];

for (let i = 0; i < 3; i++) {
  placeholders.push({} as HunterTireItem);
}

export const HunterTireTable: FunctionComponent = () => {
  const { t } = useTranslation();
  const { data = placeholders, isLoading } = useGetHunterTire();
  const [selectedIds, toggleSelectedId] = useHunterTireSelectedIds();

  return (
    <>
      <table className="border-collapse table-fixed w-full text-xs">
        <thead>
          <tr className="border-b-2 border-edge align-bottom">
            <TableHeader
              className="text-left w-[11%]"
              field="date"
              tableName="hunterTire"
            />
            <TableHeader
              className="text-left w-[14%]"
              field="vin"
              tableName="hunterTire"
            />
            <TableHeader
              className="text-left w-[19%]"
              field="vehicle"
              tableName="hunterTire"
            />
            <TableHeader
              className="text-left w-[10%]"
              field="testType"
              tableName="hunterTire"
            />
            <TableHeader
              className="text-left w-[16%]"
              field="treadDepth"
              tableName="hunterTire"
            />
            <TableHeader
              className="text-left w-[11%]"
              field="oeTireSize"
              tableName="hunterTire"
            />
            <TableHeader
              className="text-left w-[8%]"
              field="rimSize"
              tableName="hunterTire"
            />
            <th className="text-left w-[11%] px-2 py-1" />
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const vehicle = item.vehicle_info;
            const vehicleDisplay = [
              vehicle?.year,
              vehicle?.manufacturer,
              vehicle?.model,
            ]
              .filter(Boolean)
              .join(" ");
            const isSelected = selectedIds.includes(item.id);
            const vin = vehicle?.vin || item.vin;
            const isValidVin = !!String(vin).trim().match(VIN_RE);
            const handleClick = (): void => {
              if (!isLoading && item.id) {
                toggleSelectedId(item.id);
              }
            };

            return (
              <React.Fragment key={item.id ?? index}>
                <tr
                  className={`border-b border-edge align-top hover:bg-gray-100 cursor-pointer ${isSelected ? "bg-gray-100" : ""}`}
                  onClick={handleClick}
                >
                  <td className="px-2 py-1 min-w-[8em]">
                    <Placeholder loading={isLoading}>
                      <span className="flex items-center gap-1">
                        <ChevronRightIcon
                          className={`w-2 h-2 transition-transform ${isSelected ? "rotate-90" : ""}`}
                        />
                        <DateDisplay format={DEALER_DATE_TIME}>
                          {item.created_at}
                        </DateDisplay>
                      </span>
                    </Placeholder>
                  </td>
                  <td className="px-2 py-1">
                    <Placeholder loading={isLoading}>
                      {isValidVin ? vin : "-"}
                    </Placeholder>
                  </td>
                  <td className="px-2 py-1">
                    <Placeholder loading={isLoading}>
                      {vehicleDisplay || "-"}
                    </Placeholder>
                  </td>
                  <td className="px-2 py-1">
                    <Placeholder loading={isLoading}>
                      {item.test_type || "-"}
                    </Placeholder>
                  </td>
                  <td className="px-2 py-1">
                    <Placeholder loading={isLoading}>
                      <HunterTreadDepthCell
                        tireSize={item.tire_size}
                        treadDepth={item.tread_depth}
                      />
                    </Placeholder>
                  </td>
                  <td className="px-2 py-1">
                    <Placeholder loading={isLoading}>
                      {/* What our fitment data says the factory fitted; blank
                          when the vehicle never matched the TireWizard taxonomy.
                          More than one size means the trim could not be pinned
                          down, so each candidate gets its own line. */}
                      {item.oe_tire_size
                        ? item.oe_tire_size
                            .split(", ")
                            .map((size) => <div key={size}>{size}</div>)
                        : "-"}
                    </Placeholder>
                  </td>
                  <td className="px-2 py-1">
                    <Placeholder loading={isLoading}>
                      {formatRimSize(
                        item.rim_diameter,
                        item.rim_diameter_unit,
                      ) || "-"}
                    </Placeholder>
                  </td>
                  <td className="px-2 py-1">
                    <Placeholder loading={isLoading}>
                      {isValidVin ? (
                        <Link
                          className="text-primary underline hover:no-underline"
                          onClick={(e): void => e.stopPropagation()}
                          to={`${getDealerBasePath()}/product/TIRES/type/vin-search/vin/${encodeURIComponent(vin)}`}
                        >
                          {t("hunterTire.searchTires")}
                        </Link>
                      ) : null}
                    </Placeholder>
                  </td>
                </tr>
                {isSelected && <HunterTireRow id={item.id} />}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {data.length === 0 && (
        <NoData className="max-w-[12em] mx-auto my-8">
          {t("hunterTire.noData")}
        </NoData>
      )}
    </>
  );
};

/**
 * @see docs/worklog/UNTRACKED/hunter-link-chassis/rfc.md
 * @see docs/worklog/UNTRACKED/hunter-link-chassis/tfc.md
 * @see docs/worklog/TW-968-hunter-search-tires-false-vin/rfc.md
 * @see docs/worklog/TW-968-hunter-search-tires-false-vin/tfc.md
 */
